/**
 * Simply Timeline
 * Scroll-driven timeline with IntersectionObserver animations,
 * counter animations, sidebar navigation, and keyboard navigation.
 * Simply Prestige - Luxury Chocolate Theme
 */

class SimplyTimeline extends HTMLElement {
  constructor() {
    super();
    this.chapters = [];
    this.navItems = [];
    this.observer = null;
    this.scrollHandler = null;
    this.animatedChapters = new Set();
    this.currentIndex = 0;
  }

  connectedCallback() {
    this.chapters = Array.from(this.querySelectorAll('.simply-timeline__chapter'));
    this.navItems = Array.from(this.querySelectorAll('.simply-timeline__nav-item'));

    if (this.chapters.length === 0) return;

    this.splitHeadingWords();
    this.setAttribute('data-initialized', '');
    this.setupObservers();
    this.setupNavigation();
    this.setupKeyboard();
    this.setupScrollProgress();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.scrollHandler) {
      const scrollTarget = this.classList.contains('simply-timeline--snap')
        ? this
        : window;
      scrollTarget.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
  }

  // ─── Heading word splitting ────────────────────────────────────────────────

  splitHeadingWords() {
    this.chapters.forEach((chapter) => {
      const title = chapter.querySelector('.simply-timeline__title');
      if (!title || title.dataset.split === 'done') return;

      // Split title text into word spans
      const words = title.textContent.trim().split(/\s+/);
      title.innerHTML = words
        .map(
          (word, i) =>
            `<span class="word" style="animation-delay: ${(i * 0.1).toFixed(2)}s">${word}</span>`
        )
        .join(' ');

      const wordCount = words.length;
      const baseDelay = ((wordCount - 1) * 0.1).toFixed(2);

      // Cascade delays to sibling elements
      const subheading = chapter.querySelector('.simply-timeline__subheading');
      if (subheading) {
        subheading.style.animationDelay = `${(parseFloat(baseDelay) + 0.3).toFixed(2)}s`;
      }

      const body = chapter.querySelector('.simply-timeline__body');
      if (body) {
        body.style.animationDelay = `${(parseFloat(baseDelay) + 0.5).toFixed(2)}s`;
      }

      const media = chapter.querySelector('.simply-timeline__media');
      if (media) {
        media.style.animationDelay = `${(parseFloat(baseDelay) + 0.2).toFixed(2)}s`;
      }

      const stats = chapter.querySelectorAll('.simply-timeline__stat');
      stats.forEach((stat, i) => {
        stat.style.animationDelay = `${(parseFloat(baseDelay) + 0.4 + i * 0.15).toFixed(2)}s`;
      });

      const cta = chapter.querySelector('.simply-timeline__cta');
      if (cta) {
        cta.style.animationDelay = `${(parseFloat(baseDelay) + 0.7).toFixed(2)}s`;
      }

      title.dataset.split = 'done';
    });
  }

  // ─── IntersectionObserver ──────────────────────────────────────────────────

  setupObservers() {
    const isDesktop = window.innerWidth >= 1000;
    const threshold = isDesktop ? 0.15 : 0.1;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const chapter = entry.target;
            chapter.classList.add('is-visible');

            const index = this.chapters.indexOf(chapter);
            if (index !== -1) {
              this.currentIndex = index;
              this.setActiveNav(index);
              this.animateCounters(chapter);
            }
          }
        });
      },
      { threshold }
    );

    this.chapters.forEach((chapter) => this.observer.observe(chapter));
  }

  // ─── Sidebar navigation ────────────────────────────────────────────────────

  setupNavigation() {
    this.navItems.forEach((item, index) => {
      item.addEventListener('click', () => {
        const target = this.chapters[index];
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  // ─── Keyboard navigation ───────────────────────────────────────────────────

  setupKeyboard() {
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = this.chapters[this.currentIndex + 1];
        if (next) {
          next.scrollIntoView({ behavior: 'smooth' });
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = this.chapters[this.currentIndex - 1];
        if (prev) {
          prev.scrollIntoView({ behavior: 'smooth' });
        }
      }
    });
  }

  // ─── Scroll progress ───────────────────────────────────────────────────────

  setupScrollProgress() {
    const isSnapContainer = this.classList.contains('simply-timeline--snap');
    const scrollTarget = isSnapContainer ? this : window;
    const scrollRoot = isSnapContainer ? this : document.documentElement;

    this.scrollHandler = () => {
      const scrollTop = isSnapContainer ? this.scrollTop : scrollRoot.scrollTop;
      const scrollHeight = scrollRoot.scrollHeight - scrollRoot.clientHeight;
      const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;

      const navFill = this.querySelector('.simply-timeline__nav-progress-fill');
      if (navFill) {
        navFill.style.height = `${Math.min(100, Math.max(0, progress))}%`;
      }

      const mobileFill = this.querySelector('.simply-timeline__mobile-progress-fill');
      if (mobileFill) {
        mobileFill.style.width = `${Math.min(100, Math.max(0, progress))}%`;
      }
    };

    scrollTarget.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  // ─── Active nav state ──────────────────────────────────────────────────────

  setActiveNav(index) {
    this.navItems.forEach((item, i) => {
      const isActive = i === index;
      item.classList.toggle('is-active', isActive);
      if (isActive) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    });
  }

  // ─── Counter animation ─────────────────────────────────────────────────────

  animateCounters(chapter) {
    if (this.animatedChapters.has(chapter)) return;
    this.animatedChapters.add(chapter);

    const counters = chapter.querySelectorAll('[data-count-to]');
    if (counters.length === 0) return;

    const formatter = new Intl.NumberFormat('da-DK');
    const duration = 2000; // 2 seconds

    counters.forEach((el) => {
      const target = parseFloat(el.dataset.countTo);
      if (isNaN(target)) return;

      const prefix = el.dataset.countPrefix || '';
      const suffix = el.dataset.countSuffix || '';
      const startTime = performance.now();

      const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

      const tick = (now) => {
        const elapsed = now - startTime;
        const t = Math.min(elapsed / duration, 1);
        const eased = easeOutExpo(t);
        const current = Math.round(eased * target);

        el.textContent = `${prefix}${formatter.format(current)}${suffix}`;

        if (t < 1) {
          requestAnimationFrame(tick);
        } else {
          el.textContent = `${prefix}${formatter.format(target)}${suffix}`;
        }
      };

      requestAnimationFrame(tick);
    });
  }
}

// Register custom element
if (!customElements.get('simply-timeline')) {
  customElements.define('simply-timeline', SimplyTimeline);
}
